import { useRef, useState } from 'react';
import { X } from 'lucide-react';

/**
 * ModalCloseButton — standardised top-right close affordance used across all
 * modals in the app.
 *
 *   - Blue outline normally, red outline on hover / focus / active.
 *   - Scale-to-85% bounce on click (150ms ease-out).
 *   - NORMAL CLICK: fires `onClick` (the parent's onClose) INSTANTLY.
 *     The hold-to-force-close logic below is a strictly-additional escape
 *     hatch that only triggers after 1.5s of sustained press. A quick
 *     click never waits, never delays, never double-fires.
 *   - HOLD 1.5s: a pulsing red ring fills around the button; when the
 *     timer completes we call onClick AND hard-remove the closest
 *     `[role=dialog]` / modal container from the DOM as a last-resort
 *     escape for cases where the parent's onClose is somehow stuck
 *     (e.g. an API call blocking unmount).
 */
export default function ModalCloseButton({ onClick, className = '', testId = 'modal-close-btn', label = 'Close' }) {
  const [pressing, setPressing] = useState(false);
  const timerRef = useRef(null);
  const modalEl = useRef(null);

  const cancelHold = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setPressing(false);
  };

  const startHold = (e) => {
    // Capture the modal ancestor now — by the time the timer fires,
    // e.currentTarget will be null (React pools events).
    modalEl.current = e.currentTarget.closest('[role="dialog"], [data-testid$="-modal"], [data-testid^="compare-modal"]') || null;
    setPressing(true);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setPressing(false);
      // Normal close first.
      try { onClick?.(); } catch { /* ignore */ }
      // Force-remove the modal from the DOM as a hard fallback. Only runs
      // if the 1.5s timer actually completed (user deliberately held).
      if (modalEl.current && document.body.contains(modalEl.current)) {
        modalEl.current.remove();
      }
    }, 1500);
  };

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseDown={startHold}
      onMouseUp={cancelHold}
      onMouseLeave={cancelHold}
      onTouchStart={startHold}
      onTouchEnd={cancelHold}
      onTouchCancel={cancelHold}
      aria-label={label}
      data-testid={testId}
      className={`relative inline-flex items-center justify-center w-8 h-8 rounded-md border-2 border-sky-500 text-muted-foreground hover:border-red-500 hover:text-red-500 focus-visible:border-red-500 focus-visible:text-red-500 focus-visible:outline-none active:border-red-500 active:text-red-500 active:scale-[0.85] transition-all duration-150 ease-out ${className}`}
    >
      <X className="w-4 h-4" strokeWidth={2.5} />
      {/* Force-close progress ring — only renders while pressing. Animates
          stroke-dashoffset from full → 0 over 1.5s. Non-interactive so
          it never intercepts clicks. */}
      {pressing && (
        <svg
          className="pointer-events-none absolute inset-0 w-full h-full"
          viewBox="0 0 36 36"
          aria-hidden="true"
        >
          <circle
            cx="18"
            cy="18"
            r="15"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray="94.25"
            className="text-red-500"
            style={{
              animation: 'modalForceClose 1.5s linear forwards',
              transformOrigin: 'center',
              transform: 'rotate(-90deg)',
            }}
          />
          <style>{`
            @keyframes modalForceClose {
              from { stroke-dashoffset: 94.25; }
              to { stroke-dashoffset: 0; }
            }
          `}</style>
        </svg>
      )}
    </button>
  );
}
